import { el, clear } from '../../core/dom.js';
import { empty } from '../../components/empty.js';
import { errorPanel } from '../../components/error.js';
import { button } from '../../components/button.js';
import { skeleton } from '../../components/skeleton.js';
import { toast } from '../../components/toast.js';
import {
  filterOrgs, PAGE_SIZE, pickSelected, unreadTotal,
} from './model.js';
import {
  addCalendarSlot,
  getCalendar,
  getMessages,
  getSessions,
  getTenant,
  listTenants,
  markMessagesRead,
  removeCalendarSlot,
  sendMessage,
} from './actions.js';
import { renderHeader, renderSidebar } from './list.js';
import { renderDetail } from './detail.js';
import { renderCalendar } from './calendar.js';
import { openCreateOrg } from './create.js';

function blankState(opts) {
  return {
    opts,
    tenants: [],
    filtered: [],
    search: '',
    shown: PAGE_SIZE,
    selectedId: '',
    selectedTenant: null,
    messages: [],
    sessions: [],
    tab: 'overview',
    view: 'org',
    calendarDays: [],
    calendarSessions: [],
    listLoading: true,
    detailLoading: false,
    sending: false,
    fatal: '',
    live: '',
    dead: false,
    modal: null,
  };
}

function paint(ui) {
  const { state } = ui;
  clear(ui.head);
  clear(ui.layout);
  ui.head.append(renderHeader(() => openCreate(ui)));
  if (state.listLoading && !state.tenants.length) {
    ui.layout.append(skeleton(6));
  } else if (state.fatal && !state.tenants.length) {
    ui.layout.append(errorPanel({
      title: 'Organizations unavailable',
      message: state.fatal,
      action: button('Try again', { variant: 'primary', onClick: () => loadList(ui, true) }),
    }));
  } else {
    const side = renderSidebar(state, {
      onSearch: (value) => search(ui, value),
      onSelect: (id) => selectOrg(ui, id),
      onCalendar: () => openCalendar(ui),
      onMore: () => more(ui),
    });
    const panel = state.view === 'calendar'
      ? (state.detailLoading
        ? el('div', { class: 'org-detail is-loading' }, el('p', {}, 'Loading calendar…'))
        : renderCalendar(state, {
          onAddSlot: (weekday, time) => addSlot(ui, weekday, time),
          onRemoveSlot: (id) => dropSlot(ui, id),
        }))
      : renderDetail(state, {
        onTab: (tab) => setTab(ui, tab),
        onSend: (text, input) => reply(ui, text, input),
      });
    ui.layout.append(el('div', { class: 'org-layout' }, side, panel));
  }
  ui.live.textContent = state.live || '';
  const log = ui.root.querySelector('#org-thread-log');
  if (log) log.scrollTop = log.scrollHeight;
}

function openCreate(ui) {
  if (ui.state.modal) ui.state.modal.close();
  ui.state.modal = openCreateOrg({
    onCreated: () => loadList(ui, false),
  });
}

function more(ui) {
  ui.state.shown += PAGE_SIZE;
  const shown = Math.min(ui.state.shown, ui.state.filtered.length);
  ui.state.live = `Showing ${shown} of ${ui.state.filtered.length}`;
  paint(ui);
}

function search(ui, value) {
  ui.state.search = String(value || '');
  ui.state.filtered = filterOrgs(ui.state.tenants, ui.state.search);
  ui.state.shown = PAGE_SIZE;
  if (ui.state.view !== 'calendar') {
    ui.state.selectedId = pickSelected(ui.state.filtered, ui.state.selectedId);
    if (ui.state.selectedId) selectOrg(ui, ui.state.selectedId, { silent: true });
    else {
      ui.state.selectedTenant = null;
      ui.state.messages = [];
      ui.state.sessions = [];
      paint(ui);
    }
    return;
  }
  paint(ui);
}

async function loadList(ui, initial) {
  if (initial) {
    ui.state.listLoading = true;
    ui.state.fatal = '';
    paint(ui);
  }
  const data = await listTenants();
  if (ui.state.dead) return;
  ui.state.listLoading = false;
  if (!data || data.error) {
    ui.state.fatal = (data && data.error) || 'Could not load organizations';
    ui.state.live = ui.state.fatal;
    paint(ui);
    return;
  }
  ui.state.fatal = '';
  ui.state.tenants = data.tenants || [];
  ui.state.filtered = filterOrgs(ui.state.tenants, ui.state.search);
  const total = data.unread_total != null ? data.unread_total : unreadTotal(ui.state.tenants);
  ui.state.live = ui.state.tenants.length
    ? `${ui.state.tenants.length} organizations${total ? ` · ${total} unread` : ''}`
    : 'No organizations';
  if (ui.state.view === 'calendar') {
    paint(ui);
    await openCalendar(ui, { silent: true });
    return;
  }
  ui.state.selectedId = pickSelected(ui.state.filtered, ui.state.selectedId);
  if (!ui.state.selectedId) {
    ui.state.selectedTenant = null;
    ui.state.messages = [];
    ui.state.sessions = [];
    paint(ui);
    return;
  }
  await selectOrg(ui, ui.state.selectedId, { silent: true });
}

async function selectOrg(ui, tenantId, options = {}) {
  if (!tenantId) return;
  ui.state.view = 'org';
  ui.state.selectedId = tenantId;
  if (!options.silent) paint(ui);
  ui.state.detailLoading = true;
  paint(ui);
  const [detail, messages, sessions] = await Promise.all([
    getTenant(tenantId),
    getMessages(tenantId),
    getSessions(tenantId),
  ]);
  if (ui.state.dead || ui.state.selectedId !== tenantId) return;
  ui.state.detailLoading = false;
  ui.state.selectedTenant = detail && detail.tenant ? detail.tenant : null;
  ui.state.messages = (messages && messages.messages) || [];
  ui.state.sessions = (sessions && sessions.sessions) || [];
  if (ui.state.selectedTenant) {
    const listed = ui.state.tenants.find((row) => row.id === tenantId);
    if (listed) {
      if (ui.state.selectedTenant.unread_messages == null) {
        ui.state.selectedTenant.unread_messages = listed.unread_messages || 0;
      } else {
        listed.unread_messages = ui.state.selectedTenant.unread_messages || 0;
      }
    }
  }
  paint(ui);
  if (ui.state.tab === 'messages') await markRead(ui, tenantId);
}

async function setTab(ui, tab) {
  ui.state.view = 'org';
  ui.state.tab = tab || 'overview';
  paint(ui);
  if (tab === 'messages' && ui.state.selectedId) await markRead(ui, ui.state.selectedId);
}

async function markRead(ui, tenantId) {
  await markMessagesRead(tenantId);
  if (ui.state.dead) return;
  const listed = ui.state.tenants.find((row) => row.id === tenantId);
  if (listed) listed.unread_messages = 0;
  if (ui.state.selectedTenant && ui.state.selectedTenant.id === tenantId) {
    ui.state.selectedTenant.unread_messages = 0;
  }
  paint(ui);
}

async function reply(ui, text, input) {
  const tenantId = ui.state.selectedId;
  const message = String(text || '').trim();
  if (!tenantId || !message || ui.state.sending) return;
  ui.state.sending = true;
  paint(ui);
  const result = await sendMessage(tenantId, message);
  if (ui.state.dead) return;
  ui.state.sending = false;
  if (!result || result.error) {
    toast((result && result.error) || 'Could not send the message', 'error');
    paint(ui);
    return;
  }
  if (input) input.value = '';
  ui.state.messages = [...ui.state.messages, result.message];
  const listed = ui.state.tenants.find((row) => row.id === tenantId);
  if (listed) listed.unread_messages = 0;
  if (ui.state.selectedTenant) ui.state.selectedTenant.unread_messages = 0;
  ui.state.live = 'Reply sent';
  paint(ui);
}

async function openCalendar(ui, options = {}) {
  ui.state.view = 'calendar';
  ui.state.detailLoading = true;
  if (!options.silent) paint(ui);
  else paint(ui);
  const data = await getCalendar();
  if (ui.state.dead) return;
  ui.state.detailLoading = false;
  if (!data || data.error) {
    toast((data && data.error) || 'Could not load calendar', 'error');
    ui.state.calendarDays = [];
    ui.state.calendarSessions = [];
  } else {
    ui.state.calendarDays = data.days || [];
    ui.state.calendarSessions = data.sessions || [];
  }
  paint(ui);
}

async function addSlot(ui, weekday, time) {
  const result = await addCalendarSlot(weekday, time);
  if (!result || result.error) {
    toast((result && result.error) || 'Could not add slot', 'error');
    return false;
  }
  await openCalendar(ui, { silent: true });
  return true;
}

async function dropSlot(ui, slotId) {
  if (!slotId) return;
  const result = await removeCalendarSlot(slotId);
  if (!result || result.error) {
    toast((result && result.error) || 'Could not remove slot', 'error');
    return;
  }
  await openCalendar(ui, { silent: true });
}

function mount(opts) {
  const root = el('div', { class: 'orgs' });
  const ui = {
    opts,
    root,
    head: el('div'),
    layout: el('div'),
    live: el('p', { class: 'org-sr', 'aria-live': 'polite' }),
    state: blankState(opts),
  };
  root.append(ui.head, ui.layout, ui.live);
  root.addEventListener('bonnie:unmount', () => {
    ui.state.dead = true;
    if (ui.state.modal) ui.state.modal.close();
  });
  loadList(ui, true);
  return root;
}

/**
 * Super-admin Organizations screen. Uses /admin/* endpoints; the router
 * already gates the route to craftisanlabs admins.
 * @param {{ me: object, productId?: string }} opts
 */
export function renderOrganizations(opts) {
  const me = opts && opts.me;
  const allowed = !!(me && me.role === 'admin' && me.tenant_slug === 'craftisanlabs');
  if (!allowed) {
    return el('div', { class: 'orgs' }, empty({
      title: 'Not available',
      body: 'Only the BonnieOS super admin can open this screen.',
    }));
  }
  return mount(opts || {});
}
